import React from 'react';

import {
    BestEffortParameters,
    CreateWorkoutCalculatedMetricDefinitionRequestDto,
    UpdateWorkoutCalculatedMetricDefinitionRequestDto,
    WorkoutCalculatedMetricDefinitionDto,
    WorkoutMetricAlgorithm,
    WorkoutTypeV2
} from '@api/FitnessActivityClient';
import { MultiCombobox } from '@components/combobox';
import { FormSection } from '@components/form-section';
import { PageHeader } from '@components/page-header';
import { Select } from '@components/select';
import { StickyBar } from '@components/sticky-bar';
import { TextField } from '@components/textfield';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import { Label } from '@components/ui/label';
import { Switch } from '@components/ui/switch';
import { extractApiErrorMessage } from '@core/utils/errors';
import { formatEnumLabel } from '@core/utils/format-enum-label';

const FieldHint = ({ children }: { children: React.ReactNode }) => (
    <p className="text-xs text-muted-foreground">{children}</p>
);

const METRIC_KEY_REGEX = /^[a-zA-Z][a-zA-Z0-9_]*$/;

const workoutTypeOptions = Object.values(WorkoutTypeV2);

const algorithmLabels: Record<WorkoutMetricAlgorithm, string> = {
    [WorkoutMetricAlgorithm.BEST_EFFORT]: 'Best effort over a distance'
};

export interface CalculatedMetricDefinitionFormData {
    metricKey: string;
    label: string;
    algorithm: WorkoutMetricAlgorithm;
    distanceMeters: string;
    workoutTypes: WorkoutTypeV2[];
    enabled: boolean;
}

interface Props {
    title: string;
    submitLabel: string;
    submittingLabel: string;
    initialData?: CalculatedMetricDefinitionFormData;
    metricKeyLocked?: boolean;
    onCancel(): void;
    onSubmit(data: CalculatedMetricDefinitionFormData): Promise<void>;
}

export function defaultFormData(): CalculatedMetricDefinitionFormData {
    return {
        metricKey: '',
        label: '',
        algorithm: WorkoutMetricAlgorithm.BEST_EFFORT,
        distanceMeters: '',
        workoutTypes: [],
        enabled: true
    };
}

export function toFormData(
    definition: WorkoutCalculatedMetricDefinitionDto
): CalculatedMetricDefinitionFormData {
    return {
        metricKey: definition.metricKey,
        label: definition.label,
        algorithm: definition.algorithm,
        distanceMeters:
            definition.parameters.distanceMeters === undefined
                ? ''
                : String(definition.parameters.distanceMeters),
        workoutTypes: [...definition.workoutTypes],
        enabled: definition.enabled
    };
}

function toParameters(
    data: CalculatedMetricDefinitionFormData
): BestEffortParameters {
    switch (data.algorithm) {
        case WorkoutMetricAlgorithm.BEST_EFFORT:
            return {
                algorithm: WorkoutMetricAlgorithm.BEST_EFFORT,
                distanceMeters: Number(data.distanceMeters)
            };
    }
}

export function toUpdatePayload(
    data: CalculatedMetricDefinitionFormData
): UpdateWorkoutCalculatedMetricDefinitionRequestDto {
    return {
        label: data.label.trim(),
        parameters: toParameters(data),
        workoutTypes: data.workoutTypes,
        enabled: data.enabled
    };
}

export function toCreatePayload(
    data: CalculatedMetricDefinitionFormData
): CreateWorkoutCalculatedMetricDefinitionRequestDto {
    return {
        metricKey: data.metricKey.trim(),
        ...toUpdatePayload(data)
    };
}

function validateForm(data: CalculatedMetricDefinitionFormData): string | null {
    if (!data.metricKey.trim()) {
        return 'Metric key is required.';
    }
    if (!METRIC_KEY_REGEX.test(data.metricKey.trim())) {
        return 'Metric key must start with a letter and contain only letters, digits and underscores.';
    }
    if (!data.label.trim()) {
        return 'Label is required.';
    }
    if (data.algorithm === WorkoutMetricAlgorithm.BEST_EFFORT) {
        const distance = Number(data.distanceMeters);
        if (!data.distanceMeters.trim() || !Number.isFinite(distance)) {
            return 'Distance is required.';
        }
        if (distance <= 0) {
            return 'Distance must be greater than zero.';
        }
    }
    if (data.workoutTypes.length === 0) {
        return 'Select at least one workout type.';
    }
    return null;
}

export function CalculatedMetricDefinitionForm({
    title,
    submitLabel,
    submittingLabel,
    initialData,
    metricKeyLocked,
    onCancel,
    onSubmit
}: Props) {
    const [formData, setFormData] =
        React.useState<CalculatedMetricDefinitionFormData>(
            initialData || defaultFormData()
        );
    const [error, setError] = React.useState<string | null>(null);
    const [submitting, setSubmitting] = React.useState(false);

    React.useEffect(() => {
        setFormData(initialData || defaultFormData());
        setError(null);
    }, [initialData]);

    const handleChange = <K extends keyof CalculatedMetricDefinitionFormData>(
        field: K,
        value: CalculatedMetricDefinitionFormData[K]
    ) => {
        setFormData((current) => ({ ...current, [field]: value }));
        if (error) {
            setError(null);
        }
    };

    const handleSubmit = async () => {
        const validationError = validateForm(formData);
        if (validationError) {
            setError(validationError);
            return;
        }

        setSubmitting(true);
        setError(null);
        try {
            await onSubmit(formData);
        } catch (submitError) {
            setError(
                extractApiErrorMessage(
                    submitError,
                    'Failed to save the calculated metric.'
                )
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <PageHeader title={title} />

            <div className="flex flex-col gap-6 pb-24">
                <FormSection title="Definition">
                    <div className="grid gap-4 md:grid-cols-2">
                        <div className="flex flex-col gap-1.5">
                            <TextField
                                required
                                type="text"
                                label="Metric Key"
                                value={formData.metricKey}
                                disabled={submitting || metricKeyLocked}
                                onChange={(value) =>
                                    handleChange('metricKey', String(value))
                                }
                            />
                            <FieldHint>
                                {metricKeyLocked
                                    ? 'The metric key cannot be changed once created.'
                                    : 'Identifier used by challenges, e.g. bestEffort5km.'}
                            </FieldHint>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <TextField
                                required
                                type="text"
                                label="Label"
                                value={formData.label}
                                disabled={submitting}
                                onChange={(value) =>
                                    handleChange('label', String(value))
                                }
                            />
                            <FieldHint>
                                Shown to admins, e.g. Best 5 km time.
                            </FieldHint>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <Select
                                required
                                label="Algorithm"
                                value={formData.algorithm}
                                disabled={submitting}
                                options={Object.values(
                                    WorkoutMetricAlgorithm
                                ).map((algorithm) => ({
                                    label: algorithmLabels[algorithm],
                                    value: algorithm
                                }))}
                                onChange={(value: WorkoutMetricAlgorithm) =>
                                    handleChange('algorithm', value)
                                }
                            />
                            <FieldHint>
                                How the metric is derived from the cleaned GPX
                                track.
                            </FieldHint>
                        </div>

                        {formData.algorithm ===
                            WorkoutMetricAlgorithm.BEST_EFFORT && (
                            <div className="flex flex-col gap-1.5">
                                <TextField
                                    required
                                    type="number"
                                    label="Distance (metres)"
                                    value={formData.distanceMeters}
                                    disabled={submitting}
                                    onChange={(value) =>
                                        handleChange(
                                            'distanceMeters',
                                            String(value)
                                        )
                                    }
                                />
                                <FieldHint>
                                    The fastest stretch of this distance is
                                    measured.
                                </FieldHint>
                            </div>
                        )}

                        <div className="flex items-center gap-2">
                            <Switch
                                id="calculated-metric-enabled"
                                checked={formData.enabled}
                                disabled={submitting}
                                onCheckedChange={(checked) =>
                                    handleChange('enabled', checked)
                                }
                            />
                            <Label htmlFor="calculated-metric-enabled">
                                Enabled
                            </Label>
                        </div>
                    </div>

                    <div className="mt-4">
                        <MultiCombobox<WorkoutTypeV2>
                            required
                            label="Workout Types"
                            placeholder="Search workout types..."
                            searchPlaceholder="Search workout types..."
                            options={workoutTypeOptions}
                            getOptionLabel={(option) => formatEnumLabel(option)}
                            value={formData.workoutTypes}
                            disabled={submitting}
                            helperText="The metric is calculated only for these workout types."
                            onChange={(value) =>
                                handleChange('workoutTypes', value)
                            }
                        />
                    </div>
                </FormSection>

                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}
            </div>

            <StickyBar>
                <Button
                    onClick={onCancel}
                    variant="ghost"
                    disabled={submitting}
                >
                    Cancel
                </Button>
                <Button onClick={handleSubmit} loading={submitting}>
                    {submitting ? submittingLabel : submitLabel}
                </Button>
            </StickyBar>
        </>
    );
}
